"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAsyncCacheInstance = exports.useAsync = void 0;
var _react = require("react");
var _Cache = _interopRequireDefault(require("../_utils/Cache"));
const useAsyncCacheInstance = exports.useAsyncCacheInstance = new _Cache.default();
const tempPromiseDedupMemory = new Map();

/**
 * Convenience function that returns data, loading and error state of an api call.
 * Optionally can cache the api call in memory or session storage if passed the option.
 * ONLY USE CACHE WHEN IT MAKES SENSE. For example: Doesn't make sense to cache POST calls
 * @param options.cacheKey unique key for the value to cache
 * @param options.cacheTime how long the cache lives for
 * @param options.shouldCacheInSession should also store cache in session storage. ONLY STORE PUBLIC DATA IN SESSION
 * @returns data, loading, and error
 */
const useAsync = function (loadData) {
  let dependencies = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let options = arguments.length > 2 ? arguments[2] : undefined;
  const isCacheEnabled = (options === null || options === void 0 ? void 0 : options.cacheKey) !== undefined;
  const [data, setData] = (0, _react.useState)();
  const [isLoading, setIsLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)();
  (0, _react.useEffect)(() => {
    let didCancel = false;

    /**
     * This function adds caching to the useAsync. It uses in memory cache + optional saving in device storage
     * There are three cases here:
     * Case 1: This is the first call for the given cache key. This will make the api call directly
     * Case 2: This is a subsequent call for the given cache key that happened while the api call/promise is pending.
     *   In this case we prevent duplicate api calls by saving the promise in the dedup memory and clearing it once the api call resolves.
     * Case 3: This is a subsequent call for the given cache key that happened after the api call/promise resolved.
     *   In this case we just return the cached result as long as we are within the TTL.
     *   If we are over the TTL we go back to case 1
     */
    const updateWithCache = async () => {
      const {
        cacheKey,
        cacheTime,
        shouldCacheInSession
      } = options; // this code is only reached if cache is enabled aka options?.cacheKey is not undefined

      setError(undefined);
      try {
        const cachedResult = useAsyncCacheInstance.getCache(cacheKey, cacheTime, shouldCacheInSession);
        const isCachedResultUndefined = cachedResult === undefined;
        const shouldGetResultFromCache = !isCachedResultUndefined && !didCancel;
        if (shouldGetResultFromCache) {
          // Case 1: cache data is available and within TTL. Return it.
          // set isLoading from the previous rerender to false
          if (!didCancel) {
            setIsLoading(false);
            setData(cachedResult);
          }
          return;
        }
        const dedupPromise = tempPromiseDedupMemory.get(cacheKey);
        if (!!dedupPromise) {
          // Case 2: data is being fetched and this is a subsequent API call for the same key. Don't make another call/dedup it
          // Show loading status while the promise is pending

          if (!didCancel) {
            setIsLoading(true);
          }
          const result = await dedupPromise;
          tempPromiseDedupMemory.delete(cacheKey);
          if (!didCancel) {
            setIsLoading(false);
            setData(result);
          }
          return;
        }

        // Case 3: data is not available or not within TTL. Fetch it.
        if (!didCancel) {
          setIsLoading(true);
        }
        const loadDataPromise = loadData();

        // We save the promise temporarily to prevent making any unnecessary additional api calls/dedup them
        tempPromiseDedupMemory.set(cacheKey, loadDataPromise);
        const result = await loadDataPromise;
        // when the result is back, cache it
        useAsyncCacheInstance.setCache(cacheKey, result, cacheTime, shouldCacheInSession);

        // The promise now is fulfilled should route subsequent calls to case 3 or 1 depending on TTL
        // any calls there were pending from the previous case 2 will still be handled after their own promises
        // are fulfilled as JS will resume execution where it left off after the await for case 2
        tempPromiseDedupMemory.delete(cacheKey);
        if (!didCancel) {
          setData(result);
        }
      } catch (error) {
        // clear the cache not to always return the same errors
        useAsyncCacheInstance.clearCache(cacheKey, shouldCacheInSession);
        tempPromiseDedupMemory.delete(cacheKey);
        if (!didCancel) {
          setError(error);
        }
      }
      if (!didCancel) {
        setIsLoading(false);
      }
    };
    const update = async () => {
      setIsLoading(true);
      setError(undefined);
      try {
        const result = await loadData();
        if (!didCancel) {
          setData(result);
        }
      } catch (error) {
        if (!didCancel) {
          setError(error);
        }
      }
      if (!didCancel) {
        setIsLoading(false);
      }
    };
    if (isCacheEnabled) {
      updateWithCache();
    } else {
      update();
    }
    return () => {
      didCancel = true;
    };
    // Allow passing arbitrary dependencies for re-triggering the hook.
    // See examples in the docs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  return {
    data,
    isLoading,
    error
  };
};
exports.useAsync = useAsync;